import React from "react";
import { Link } from "react-router-dom";
import { X, Trash2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { imageUrl, formatPrice } from "@/lib/api";

export default function CartDrawer({ open, onClose }) {
  const { items, removeItem, total } = useCart();

  return (
    <div
      data-testid="cart-drawer"
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-[#2D2926]/30 backdrop-blur-sm"
        onClick={onClose}
        data-testid="cart-overlay"
      />
      <aside
        className={`absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#F8F5F2] shadow-2xl transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between px-8 py-6 border-b border-[#E3DDD7]">
          <h2 className="font-serif text-2xl text-[#2D2926]">Your Cart</h2>
          <button
            data-testid="cart-close-btn"
            onClick={onClose}
            className="p-1 hover:text-[#E25C46]"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <p className="label-eyebrow mb-3">Empty</p>
              <p className="text-[#5B5653] text-sm">
                Your cart awaits its first piece of country.
              </p>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="flex gap-4 pb-6 border-b border-[#E3DDD7]"
                >
                  <div className="w-20 h-24 bg-[#EFEBE6] overflow-hidden flex-shrink-0">
                    <img
                      src={imageUrl(item.image_path)}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <p className="font-serif text-lg text-[#2D2926]">{item.title}</p>
                      <p className="text-xs text-[#5B5653] mt-1">{item.size}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#2D2926]">{formatPrice(item.price)}</span>
                      <button
                        data-testid={`cart-remove-${item.id}`}
                        onClick={() => removeItem(item.id)}
                        className="text-[#5B5653] hover:text-[#B73225] transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-8 py-6 border-t border-[#E3DDD7] space-y-4">
            <div className="flex justify-between text-sm text-[#5B5653]">
              <span>Delivery</span>
              <span>Free across Australia</span>
            </div>
            <div className="flex justify-between text-base text-[#2D2926] font-medium">
              <span className="label-eyebrow">Total</span>
              <span data-testid="cart-total" className="font-serif text-2xl">
                {formatPrice(total)}
              </span>
            </div>
            <Link
              to="/checkout"
              data-testid="checkout-btn"
              onClick={onClose}
              className="btn-primary w-full"
            >
              Proceed to Checkout
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
}
